import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LogOut, Archive, Plus } from "lucide-react";
import { EmailCentre } from "@/pages/EmailCentre";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border-b border-[#1A1A1A]/25 py-2 text-sm font-light placeholder:text-[#1A1A1A]/35 focus:outline-none focus:border-[#4A148C] transition-colors duration-300";

const ProductRow = ({ product, token, onChange }) => {
  const [form, setForm] = useState({
    name: product.name,
    strap: product.strap,
    tag: product.tag,
    image: product.image,
    price: (product.price_pence / 100).toFixed(2),
    stock: product.stock ?? 100,
    trade_price: ((product.trade_price_pence ?? 0) / 100).toFixed(2),
    coming_soon: product.coming_soon || false,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/products/${product.lookup_key}`,
        { ...form, price_pence: Math.round(parseFloat(form.price) * 100), stock: Math.max(0, parseInt(form.stock) || 0), trade_price_pence: Math.max(0, Math.round(parseFloat(form.trade_price || "0") * 100)), coming_soon: form.coming_soon },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${form.name} saved`);
      onChange();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    try {
      await axios.delete(`${API}/admin/products/${product.lookup_key}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${product.name} archived`);
      onChange();
    } catch {
      toast.error("Archive failed");
    }
  };

  return (
    <div
      className={`border border-[#E5DFD3] bg-[#FDFBF7] p-6 ${product.active === false ? "opacity-40" : ""}`}
      data-testid={`admin-product-${product.lookup_key}`}
    >
      <div className="flex gap-6">
        <div className="w-24 h-24 bg-[#EFEBE1] border border-[#E5DFD3] shrink-0">
          <img src={form.image} alt={form.name} className="w-full h-full object-cover mix-blend-multiply" />
        </div>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className={inputCls} data-testid={`admin-name-${product.lookup_key}`} />
          <input value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} placeholder="Tag (e.g. The Original)" className={inputCls} />
          <input value={form.strap} onChange={(e) => setForm({ ...form, strap: e.target.value })} placeholder="Strapline" className={`${inputCls} sm:col-span-2`} />
          <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="Image URL" className={inputCls} data-testid={`admin-image-${product.lookup_key}`} />
          <div className="flex items-center gap-2">
            <span className="text-sm text-[#1A1A1A]/50">£</span>
            <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price" className={inputCls} data-testid={`admin-price-${product.lookup_key}`} />
            <span className="text-sm text-[#1A1A1A]/50 whitespace-nowrap">Stock</span>
            <input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock" className={`${inputCls} w-20`} data-testid={`admin-stock-${product.lookup_key}`} />
            <span className="text-sm text-[#1A1A1A]/50 whitespace-nowrap">Trade £</span>
            <input value={form.trade_price} onChange={(e) => setForm({ ...form, trade_price: e.target.value })} placeholder="Trade" className={`${inputCls} w-20`} data-testid={`admin-trade-price-${product.lookup_key}`} />
          </div>
        </div>
      </div>
      <div className="mt-4 flex gap-3 justify-end items-center">
        <label className="mr-auto inline-flex items-center gap-2 text-[10px] font-semibold tracking-[0.2em] uppercase text-[#1A1A1A]/60 cursor-pointer" data-testid={`admin-coming-soon-label-${product.lookup_key}`}>
          <input
            type="checkbox"
            checked={form.coming_soon}
            onChange={(e) => setForm({ ...form, coming_soon: e.target.checked })}
            data-testid={`admin-coming-soon-${product.lookup_key}`}
            className="w-4 h-4 accent-[#4A148C]"
          />
          Coming soon
        </label>
        {product.active !== false && (
          <button onClick={archive} data-testid={`admin-archive-${product.lookup_key}`} className="inline-flex items-center gap-2 border border-[#1A1A1A]/30 px-4 py-2 text-[10px] font-semibold tracking-[0.2em] uppercase hover:border-[#4A148C] hover:text-[#4A148C] transition-colors duration-300">
            <Archive size={12} /> Archive
          </button>
        )}
        <button onClick={save} disabled={saving} data-testid={`admin-save-${product.lookup_key}`} className="bg-[#1A1A1A] text-[#FDFBF7] px-6 py-2 text-[10px] font-semibold tracking-[0.2em] uppercase hover:bg-[#4A148C] disabled:opacity-50 transition-colors duration-300">
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
};

const DispatchForm = ({ orderId, onDispatch }) => {
  const [courier, setCourier] = useState("royal_mail");
  const [code, setCode] = useState("");
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={`dispatch-form-${orderId}`}>
      <select
        value={courier}
        onChange={(e) => setCourier(e.target.value)}
        data-testid={`courier-select-${orderId}`}
        className="border border-[#E5DFD3] bg-[#FDFBF7] px-3 py-2 text-xs font-light focus:outline-none focus:border-[#4A148C]"
      >
        <option value="royal_mail">Royal Mail</option>
        <option value="evri">Evri</option>
        <option value="dpd">DPD</option>
        <option value="other">Other</option>
      </select>
      <input
        value={code}
        onChange={(e) => setCode(e.target.value)}
        placeholder="Tracking code (optional)"
        className="border border-[#E5DFD3] bg-[#FDFBF7] px-3 py-2 text-xs font-light w-44 focus:outline-none focus:border-[#4A148C]"
        data-testid={`tracking-input-${orderId}`}
      />
      <button
        onClick={() => onDispatch(orderId, courier, code)}
        data-testid={`dispatch-button-${orderId}`}
        className="bg-[#1A1A1A] text-[#FDFBF7] px-6 py-2 text-[10px] font-semibold tracking-[0.2em] uppercase hover:bg-[#4A148C] transition-colors duration-300"
      >
        Mark dispatched
      </button>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("proppa_admin_token") || "");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("products");
  const [orders, setOrders] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [tradeAccounts, setTradeAccounts] = useState([]);
  const [newAccount, setNewAccount] = useState({ business_name: "", email: "", password: "" });
  const [newProduct, setNewProduct] = useState({ lookup_key: "", name: "", price: "", image: "", tag: "", strap: "" });

  const loadOrders = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(res.data);
    } catch {
      /* token invalid — products loader handles sign-out */
    }
  };

  const loadEnquiries = async () => {
    try {
      const res = await axios.get(`${API}/admin/enquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setEnquiries(res.data);
    } catch {
      /* same */
    }
  };

  const loadTradeAccounts = async () => {
    try {
      const res = await axios.get(`${API}/admin/trade-accounts`, { headers: { Authorization: `Bearer ${token}` } });
      setTradeAccounts(res.data);
    } catch {
      /* same */
    }
  };

  const createTradeAccount = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/trade-accounts`, newAccount, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Trade account created for ${newAccount.business_name}`);
      setNewAccount({ business_name: "", email: "", password: "" });
      loadTradeAccounts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    }
  };

  const deleteTradeAccount = async (email) => {
    try {
      await axios.delete(`${API}/admin/trade-accounts/${email}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${email} removed`);
      loadTradeAccounts();
    } catch {
      toast.error("Could not remove account");
    }
  };

  const dispatchOrder = async (orderId, courier, trackingCode) => {
    try {
      await axios.put(
        `${API}/admin/orders/${orderId}/dispatch`,
        { courier, tracking_code: trackingCode || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${orderId} marked as dispatched`);
      loadOrders();
    } catch {
      toast.error("Could not update order");
    }
  };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(res.data);
    } catch {
      setToken("");
      sessionStorage.removeItem("proppa_admin_token");
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (token && tab === "orders") loadOrders();
    if (token && tab === "enquiries") loadEnquiries();
    if (token && tab === "trade") loadTradeAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, token]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, login);
      sessionStorage.setItem("proppa_admin_token", res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${API}/admin/products`,
        { ...newProduct, price_pence: Math.round(parseFloat(newProduct.price) * 100) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${newProduct.name} added`);
      setNewProduct({ lookup_key: "", name: "", price: "", image: "", tag: "", strap: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add product");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-[#FDFBF7]" data-testid="admin-login-page">
        <form onSubmit={doLogin} className="max-w-sm w-full border border-[#E5DFD3] p-10" data-testid="admin-login-form">
          <p className="font-serif text-3xl font-light tracking-tight">
            Proppa <span className="italic text-[#4A148C]">Foods</span> <span className="text-sm text-[#6E6B65]">admin</span>
          </p>
          <div className="mt-8 space-y-6">
            <input type="email" required value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} placeholder="Email" className={inputCls} data-testid="admin-email-input" />
            <input type="password" required value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} placeholder="Password" className={inputCls} data-testid="admin-password-input" />
          </div>
          {loginError && <p className="mt-4 text-xs text-[#4A148C]" data-testid="admin-login-error">{loginError}</p>}
          <button type="submit" data-testid="admin-login-button" className="mt-8 w-full bg-[#1A1A1A] text-[#FDFBF7] px-8 py-4 text-xs font-semibold tracking-[0.2em] uppercase hover:bg-[#4A148C] transition-colors duration-300">
            Sign in
          </button>
          <Link to="/" className="mt-6 inline-flex items-center gap-2 text-xs text-[#1A1A1A]/50 hover:text-[#4A148C] transition-colors" data-testid="admin-back-link">
            <ArrowLeft size={12} /> Back to shop
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-16 px-6" data-testid="admin-dashboard">
      <div className="max-w-[1000px] mx-auto">
        <div className="flex items-center justify-between mb-12">
          <div className="flex gap-2" data-testid="admin-tabs">
            <button
              onClick={() => setTab("products")}
              data-testid="admin-products-tab"
              className={`px-6 py-3 text-[10px] font-semibold tracking-[0.2em] uppercase border transition-colors duration-300 ${
                tab === "products" ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]" : "border-[#1A1A1A]/30 hover:border-[#4A148C] hover:text-[#4A148C]"
              }`}
            >
              Products
            </button>
            <button
              onClick={() => setTab("orders")}
              data-testid="admin-orders-tab"
              className={`px-6 py-3 text-[10px] font-semibold tracking-[0.2em] uppercase border transition-colors duration-300 ${
                tab === "orders" ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]" : "border-[#1A1A1A]/30 hover:border-[#4A148C] hover:text-[#4A148C]"
              }`}
            >
              Orders
            </button>
            <button
              onClick={() => setTab("enquiries")}
              data-testid="admin-enquiries-tab"
              className={`px-6 py-3 text-[10px] font-semibold tracking-[0.2em] uppercase border transition-colors duration-300 ${
                tab === "enquiries" ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]" : "border-[#1A1A1A]/30 hover:border-[#4A148C] hover:text-[#4A148C]"
              }`}
            >
              Enquiries
            </button>
            <button
              onClick={() => setTab("trade")}
              data-testid="admin-trade-tab"
              className={`px-6 py-3 text-[10px] font-semibold tracking-[0.2em] uppercase border transition-colors duration-300 ${
                tab === "trade" ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]" : "border-[#1A1A1A]/30 hover:border-[#4A148C] hover:text-[#4A148C]"
              }`}
            >
              Trade
            </button>
            <button
              onClick={() => setTab("emails")}
              data-testid="admin-emails-tab"
              className={`px-6 py-3 text-[10px] font-semibold tracking-[0.2em] uppercase border transition-colors duration-300 ${
                tab === "emails" ? "bg-[#1A1A1A] text-[#FDFBF7] border-[#1A1A1A]" : "border-[#1A1A1A]/30 hover:border-[#4A148C] hover:text-[#4A148C]"
              }`}
            >
              Emails
            </button>
          </div>
          <div className="flex gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-xs text-[#1A1A1A]/50 hover:text-[#4A148C] transition-colors" data-testid="admin-back-to-shop">
              <ArrowLeft size={12} /> Shop
            </Link>
            <button
              onClick={() => { sessionStorage.removeItem("proppa_admin_token"); setToken(""); }}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 text-xs text-[#1A1A1A]/50 hover:text-[#4A148C] transition-colors"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>

        {tab === "products" && (
        <>
        <div className="space-y-4">
          {products.map((p) => (
            <ProductRow key={p.lookup_key} product={p} token={token} onChange={load} />
          ))}
        </div>

        <form onSubmit={addProduct} className="mt-12 border border-[#E5DFD3] p-6" data-testid="admin-new-product-form">
          <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#4A148C] mb-6">
            <Plus size={12} className="inline mr-2" />Add a product
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            <input required value={newProduct.lookup_key} onChange={(e) => setNewProduct({ ...newProduct, lookup_key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} placeholder="lookup_key (e.g. lime_pickle)" className={inputCls} data-testid="admin-new-key-input" />
            <input required value={newProduct.name} onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="Name" className={inputCls} data-testid="admin-new-name-input" />
            <input required value={newProduct.price} onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })} placeholder="Price £ (e.g. 3.50)" className={inputCls} data-testid="admin-new-price-input" />
            <input required value={newProduct.image} onChange={(e) => setNewProduct({ ...newProduct, image: e.target.value })} placeholder="Image URL" className={inputCls} data-testid="admin-new-image-input" />
            <input value={newProduct.tag} onChange={(e) => setNewProduct({ ...newProduct, tag: e.target.value })} placeholder="Tag" className={inputCls} />
            <input value={newProduct.strap} onChange={(e) => setNewProduct({ ...newProduct, strap: e.target.value })} placeholder="Strapline" className={inputCls} />
          </div>
          <button type="submit" data-testid="admin-new-submit-button" className="mt-6 bg-[#1A1A1A] text-[#FDFBF7] px-8 py-3 text-[10px] font-semibold tracking-[0.2em] uppercase hover:bg-[#4A148C] transition-colors duration-300">
            Add product
          </button>
        </form>
        </>
        )}

        {tab === "orders" && (
          <div className="space-y-4" data-testid="admin-orders-list">
            {orders.length === 0 && (
              <p className="text-sm font-light text-[#1A1A1A]/50" data-testid="admin-orders-empty">
                No paid orders yet.
              </p>
            )}
            {orders.map((o) => (
              <div key={o.order_id} className="border border-[#E5DFD3] bg-[#FDFBF7] p-6" data-testid={`admin-order-${o.order_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <p className="font-serif text-xl">{o.order_id}</p>
                    <p className="text-xs font-light text-[#1A1A1A]/50 mt-1">
                      {o.created_at ? new Date(o.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                      {o.customer_email ? ` · ${o.customer_email}` : ""}
                    </p>
                  </div>
                  <span
                    className={`px-3 py-1 text-[10px] font-semibold tracking-[0.2em] uppercase ${
                      o.fulfillment === "dispatched" ? "bg-[#4A148C] text-[#FDFBF7]" : "bg-[#FF6B00] text-[#1A1A1A]"
                    }`}
                    data-testid={`order-status-${o.order_id}`}
                  >
                    {o.fulfillment === "dispatched" ? "Dispatched" : "To pack"}
                  </span>
                </div>
                <ul className="text-sm font-light text-[#1A1A1A]/70 space-y-1 mb-4">
                  {o.items.map((i, idx) => (
                    <li key={idx} className="flex justify-between">
                      <span>{i.name} × {i.quantity}</span>
                      <span>{i.free ? "FREE" : `£${((i.unit_amount * i.quantity) / 100).toFixed(2)}`}</span>
                    </li>
                  ))}
                </ul>
                {o.shipping && (
                  <p className="text-xs font-light text-[#1A1A1A]/50 mb-4" data-testid={`order-shipping-${o.order_id}`}>
                    Ship to: {o.shipping.name}, {o.shipping.line1}{o.shipping.line2 ? `, ${o.shipping.line2}` : ""}, {o.shipping.city}, {o.shipping.postal_code}
                  </p>
                )}
                <div className="flex flex-wrap items-center justify-between gap-4 border-t border-[#E5DFD3] pt-4">
                  <span className="font-serif text-lg">£{(o.amount / 100).toFixed(2)}</span>
                  {o.fulfillment !== "dispatched" ? (
                    <DispatchForm orderId={o.order_id} onDispatch={dispatchOrder} />
                  ) : (
                    <span className="text-xs font-light text-[#1A1A1A]/50" data-testid={`tracking-info-${o.order_id}`}>
                      {o.tracking_url ? (
                        <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="text-[#4A148C] hover:underline">
                          Track ({o.tracking_code})
                        </a>
                      ) : (
                        "Dispatched"
                      )}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-4" data-testid="admin-enquiries-list">
            {enquiries.length === 0 && (
              <p className="text-sm font-light text-[#1A1A1A]/50" data-testid="admin-enquiries-empty">
                No wholesale enquiries yet.
              </p>
            )}
            {enquiries.map((e) => (
              <div key={e.enquiry_id} className="border border-[#E5DFD3] bg-[#FDFBF7] p-6" data-testid={`admin-enquiry-${e.enquiry_id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                  <div>
                    <p className="font-serif text-xl">{e.business_name}</p>
                    <p className="text-xs font-light text-[#1A1A1A]/50 mt-1">
                      {e.contact_name} · <a href={`mailto:${e.email}`} className="text-[#4A148C] hover:underline">{e.email}</a>
                      {e.created_at ? ` · ${new Date(e.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}` : ""}
                    </p>
                  </div>
                  <span className="px-3 py-1 text-[10px] font-semibold tracking-[0.2em] uppercase bg-[#EFEBE1] text-[#1A1A1A]/60">
                    {e.enquiry_id}
                  </span>
                </div>
                <p className="text-sm font-light text-[#1A1A1A]/70 whitespace-pre-wrap">{e.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "trade" && (
          <div className="space-y-4" data-testid="admin-trade-section">
            <form onSubmit={createTradeAccount} className="border border-[#E5DFD3] p-6" data-testid="admin-trade-form">
              <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#4A148C] mb-6">Create a trade account</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4">
                <input required value={newAccount.business_name} onChange={(e) => setNewAccount({ ...newAccount, business_name: e.target.value })} placeholder="Business name (e.g. The Crown & Anchor)" className={inputCls} data-testid="trade-business-input" />
                <input required type="email" value={newAccount.email} onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })} placeholder="Their email" className={inputCls} data-testid="trade-account-email-input" />
                <input required minLength={8} value={newAccount.password} onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })} placeholder="Password (min 8 chars)" className={inputCls} data-testid="trade-account-password-input" />
              </div>
              <button type="submit" data-testid="trade-create-button" className="mt-6 bg-[#1A1A1A] text-[#FDFBF7] px-8 py-3 text-[10px] font-semibold tracking-[0.2em] uppercase hover:bg-[#4A148C] transition-colors duration-300">
                Create account
              </button>
              <p className="mt-3 text-[10px] font-light text-[#1A1A1A]/40">They sign in at /trade — share the link and password with them directly.</p>
            </form>
            {tradeAccounts.map((a) => (
              <div key={a.email} className="border border-[#E5DFD3] bg-[#FDFBF7] p-6 flex items-center justify-between" data-testid={`trade-account-${a.email}`}>
                <div>
                  <p className="font-serif text-xl">{a.business_name}</p>
                  <p className="text-xs font-light text-[#1A1A1A]/50 mt-1">{a.email}</p>
                </div>
                <button onClick={() => deleteTradeAccount(a.email)} data-testid={`trade-delete-${a.email}`} className="border border-[#1A1A1A]/30 px-4 py-2 text-[10px] font-semibold tracking-[0.2em] uppercase hover:border-[#4A148C] hover:text-[#4A148C] transition-colors duration-300">
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
        {tab === "emails" && <EmailCentre token={token} />}
      </div>
    </div>
  );
}
